import React, { useContext } from 'react'
import { FormikValues, getIn, useFormikContext } from 'formik'
import Select, { SingleValue } from 'react-select'
import FieldFrame, { errorId } from './FieldFrame'
import { FormReadOnlyContext } from './FormReadOnlyContext'
import { SelectOption } from './SelectOption'
import { useFieldError } from './useFieldError'

const invalidClass = (error?: string) => (error ? 'is-invalid' : '')

export const TextField = ({
  name,
  label,
  placeholder
}: {
  name: string
  label: string
  placeholder?: string
}) => {
  const { values, handleChange, handleBlur } = useFormikContext<FormikValues>()
  const { error, clearServerError, describedBy, invalid } = useFieldError(name)
  const readOnly = useContext(FormReadOnlyContext)

  return (
    <FieldFrame name={name} label={label} error={error}>
      <input
        id={name}
        name={name}
        type="text"
        autoComplete="off"
        placeholder={placeholder}
        disabled={readOnly}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        value={getIn(values, name) ?? ''}
        onChange={(event) => {
          clearServerError()
          handleChange(event)
        }}
        onBlur={handleBlur}
        className={`form-control inverted ${invalidClass(error)}`}
      />
    </FieldFrame>
  )
}

export const TextareaField = ({
  name,
  label,
  placeholder,
  rows = 3
}: {
  name: string
  label: string
  placeholder?: string
  rows?: number
}) => {
  const { values, handleChange, handleBlur } = useFormikContext<FormikValues>()
  const { error, clearServerError, describedBy, invalid } = useFieldError(name)
  const readOnly = useContext(FormReadOnlyContext)

  return (
    <FieldFrame name={name} label={label} error={error}>
      <textarea
        id={name}
        name={name}
        rows={rows}
        autoComplete="off"
        placeholder={placeholder}
        disabled={readOnly}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        value={getIn(values, name) ?? ''}
        onChange={(event) => {
          clearServerError()
          handleChange(event)
        }}
        onBlur={handleBlur}
        className={`form-control inverted ${invalidClass(error)}`}
      />
    </FieldFrame>
  )
}

export const SelectField = <Value extends string>({
  name,
  options,
  placeholder,
  label,
  tooltip,
  ariaLabel,
  disabled = false,
  inverted = false
}: {
  name: string
  options: SelectOption<Value>[]
  placeholder: string
  label?: string
  tooltip?: string
  ariaLabel?: string
  disabled?: boolean
  // White input for fields sitting on the grey card rather than inside a white panel
  inverted?: boolean
}) => {
  const { values, setFieldValue, setFieldTouched } =
    useFormikContext<FormikValues>()
  const { error, clearServerError, describedBy, invalid } = useFieldError(name)
  const readOnly = useContext(FormReadOnlyContext)
  const selected: unknown = getIn(values, name)

  return (
    <FieldFrame name={name} label={label} tooltip={tooltip} error={error}>
      <Select<SelectOption<Value>>
        inputId={name}
        name={name}
        classNamePrefix="react-select"
        className={`react-select ${inverted ? 'inverted' : ''} ${invalidClass(error)}`}
        options={options}
        placeholder={placeholder}
        isSearchable={false}
        isDisabled={disabled || readOnly}
        aria-label={ariaLabel}
        aria-invalid={invalid}
        aria-errormessage={describedBy}
        value={options.find((option) => option.value === selected) ?? null}
        onChange={(option: SingleValue<SelectOption<Value>>) => {
          clearServerError()
          setFieldTouched(name, true, false)
          setFieldValue(name, option?.value ?? '')
        }}
        onBlur={() => setFieldTouched(name, true)}
      />
    </FieldFrame>
  )
}

export const CheckboxField = ({
  name,
  label,
  ariaLabel,
  variant = 'checkbox'
}: {
  name: string
  label?: string
  ariaLabel?: string
  variant?: 'checkbox' | 'switch'
}) => {
  const { values, setFieldValue } = useFormikContext<FormikValues>()
  const { error, clearServerError, describedBy, invalid } = useFieldError(name)
  const readOnly = useContext(FormReadOnlyContext)
  const isSwitch = variant === 'switch'

  return (
    <div className={`form-check ${isSwitch ? 'robis-switch' : ''}`}>
      <input
        id={name}
        name={name}
        type="checkbox"
        role={isSwitch ? 'switch' : undefined}
        disabled={readOnly}
        aria-label={ariaLabel}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        checked={!!getIn(values, name)}
        onChange={(event) => {
          clearServerError()
          setFieldValue(name, event.target.checked)
        }}
        className="form-check-input"
      />
      {label && (
        <label
          className={`form-check-label ${isSwitch ? 'ms-2' : ''}`}
          htmlFor={name}
        >
          {label}
        </label>
      )}
      {error && (
        <div id={errorId(name)} className="invalid-feedback d-block">
          {error}
        </div>
      )}
    </div>
  )
}
